<script setup>
import { computed, ref, onMounted, watch, defineOptions, provide } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import CreateOrLinkIssue from './CreateOrLinkIssue.vue';
import CrmAPI from 'dashboard/api/integrations/crm';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import Issue from './Issue.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  integrationId: {
    type: String,
    required: true,
  },
  integrationName: {
    type: String,
    required: true,
  },
  headerButton: {
    type: Boolean,
    default: false,
  },
});

defineOptions({
  name: 'CRM',
});

const getters = useStoreGetters();
const { t } = useI18n();
const CrmAPIIntegration = new CrmAPI(props.integrationId);
const linkedIssue = ref(null);
const loading = ref(true);
const shouldShow = ref(false);
const shouldShowPopup = ref(false);
const isUnlinking = ref(false);

provide('isUnlinking', isUnlinking);

const currentAccountId = getters.getCurrentAccountId;

const conversation = computed(() =>
  getters.getConversationById.value(props.conversationId)
);

const isSameStage = (firstStageId, secondStageId) => {
  if (firstStageId == null || secondStageId == null) return false;
  return String(firstStageId) === String(secondStageId);
};

const bgClass = computed(() => {
  if (!linkedIssue.value) return '';

  const dealStageId = linkedIssue.value.deal_stage?.id;
  const pipelineStages = Object.values(
    linkedIssue.value.pipeline_stages || {}
  );
  if (!dealStageId) return '';

  const matchedStage = pipelineStages.find(stage =>
    isSameStage(stage.id, dealStageId)
  );
  const status =
    matchedStage?.code || linkedIssue.value.deal_stage?.code || null;

  if (status === 'won') return 'bg-green-600 dark:bg-green-600 rounded-md';
  if (status === 'lost') return 'bg-red-600 dark:bg-red-600 rounded-md';
  return '';
});

const textColorClass = computed(() => {
  if (!linkedIssue.value) return '';

  const dealStageId = linkedIssue.value.deal_stage?.id;
  const pipelineStages = Object.values(
    linkedIssue.value.pipeline_stages || {}
  );
  if (!dealStageId) return '';

  const matchedStage = pipelineStages.find(stage =>
    isSameStage(stage.id, dealStageId)
  );
  const status =
    matchedStage?.code || linkedIssue.value.deal_stage?.code || null;

  if (status === 'won' || status === 'lost') return 'text-white';
  return '';
});

const hoverClass = computed(() => {
  if (!linkedIssue.value) return '';

  const dealStageId = linkedIssue.value.deal_stage?.id;
  const pipelineStages = Object.values(
    linkedIssue.value.pipeline_stages || {}
  );
  if (!dealStageId) return '';

  const matchedStage = pipelineStages.find(stage =>
    isSameStage(stage.id, dealStageId)
  );
  const status =
    matchedStage?.code || linkedIssue.value.deal_stage?.code || null;

  if (status === 'won') {
    return 'hover:!bg-green-700 dark:hover:!bg-green-700';
  }
  if (status === 'lost') {
    return 'hover:!bg-red-700 dark:hover:!bg-red-700';
  }
  return '';
});

const loadLinkedIssue = async ({ keepCardOpen = false } = {}) => {
  const name = '%23' + props.conversationId;
  const dealId = conversation.value.additional_attributes?.crm_lead_id;

  if (!keepCardOpen) {
    linkedIssue.value = null;
    loading.value = true;
  }

  try {
    const response = await CrmAPIIntegration.getLinkedDeal({dealId, name});
    const deals = response.data?.data?.deals || [];

    let foundDeal = {};

    if(dealId) {
      foundDeal = deals.find(deal => deal.id == dealId);
    } else {
      foundDeal = deals.find(deal =>
        deal.name.includes('#' + props.conversationId)
      );
    }

    if (foundDeal) {
      linkedIssue.value = foundDeal;
    }

    loading.value = false;
  } catch (error) {
    // We don't want to show an error message here, as it's not critical. When someone clicks on the Linear icon, we can inform them that the integration is disabled.
    loading.value = false;
  }
};

const refreshLinkedIssue = () => loadLinkedIssue({ keepCardOpen: true });

const openIssue = () => {
  if (!linkedIssue.value) shouldShowPopup.value = true;
  shouldShow.value = true;
};

const closePopup = () => {
  shouldShowPopup.value = false;
  loadLinkedIssue();
};

const closeIssue = () => {
  shouldShow.value = false;
};

watch(
  () => props.conversationId,
  () => {
    loadLinkedIssue();
  }
);

onMounted(() => {
  loadLinkedIssue();
});
</script>

<template>
  <div>
    <Spinner
      v-if="loading"
      :size="16"
      class="text-gray-600 dark:text-gray-300"
    />
    <div v-else :class="['relative', { group: linkedIssue }, bgClass]">
      <woot-button
        v-if="!linkedIssue || headerButton"
        v-on-clickaway="closeIssue"
        variant="clear"
        color-scheme="secondary"
        :class="hoverClass"
        @click="openIssue"
      >
        <img v-if="props.integrationId === 'rdstation'"
          width="19"
          src="./icons/rdstation.jpeg"
          class="rounded-full mr-1"
        />
        <img v-else-if="props.integrationId === 'welloncrm'"
          width="19"
          src="./icons/welloncrm.png"
          class="rounded-full mr-1"
        />
        <span
          v-if="linkedIssue && headerButton"
          class="crm-deal-name text-xs font-medium text-ash-800"
          :class="[textColorClass]"
        >
          {{ linkedIssue.name }}
        </span>
        <span v-else class="text-xs font-medium text-ash-800">{{ integrationName }}</span>
      </woot-button>
      <Issue
        v-if="linkedIssue"
        :deal="linkedIssue"
        :integration-id="props.integrationId"
        :text-color="textColorClass"
        :class="
          headerButton
            ? 'absolute left-1/2 top-[40px] -translate-x-1/2 invisible w-[28rem] max-w-[calc(100vw-2rem)] group-hover:visible'
            : ''
        "
        @stage-updated="refreshLinkedIssue"
        @products-updated="refreshLinkedIssue"
        @assignee-updated="refreshLinkedIssue"
      />
      <woot-modal
        v-model:show="shouldShowPopup"
        :on-close="closePopup"
        :close-on-backdrop-click="false"
        class="!items-start [&>div]:!top-12 [&>div]:sticky [&>div]:!w-[52rem] [&>div]:max-w-[calc(100vw-2rem)]"
      >
        <CreateOrLinkIssue
          :conversation="conversation"
          :account-id="currentAccountId"
          :integration-id="props.integrationId"
          @close="closePopup"
        />
      </woot-modal>
    </div>
  </div>
</template>
